import getAxiosInstance from '@/utils/axios'
import { getProxyLocation } from '@/utils'
import { auth } from '@/auth'
import retry from 'async-retry'
import type { InternalAxiosRequestConfig } from 'axios'

const authInterceptedInstance = getAxiosInstance()

const getPulsarAccessToken = () => {
  const { audience } = useInstance()

  return retry(
    async () => {
      return await getTokenForAudience(audience.value)
    },
    { retries: 3, minTimeout: 1000, factor: 2 }
  )
}

const getTokenForAudience = async (audience: string | undefined) => {
  const { getAccessTokenSilently } = auth
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: audience,
      scope: 'admin'
    }
  })

  if (token.length > 50 && token.indexOf('..') < 0) {
    return token
  } else {
    throw Error('invalid token')
  }
}

const configureAxiosRequestInterceptor = async (
  tokenFunction: () => Promise<string>,
  config: InternalAxiosRequestConfig
) => {
  config.headers['Authorization'] = `Bearer ${await tokenFunction()}`
  return config
}

authInterceptedInstance.interceptors.request.use(async config => {
  // Check for serviceAccountName in FormData (Pulsar connectors) or JSON body (Kafka connectors)
  let serviceAccountName: string | null = null
  if (config.data && typeof config.data.get === 'function') {
    serviceAccountName = config.data.get('serviceAccountName')
  } else if (config.data?.serviceAccountName) {
    serviceAccountName = config.data.serviceAccountName
    delete config.data.serviceAccountName
  }

  if (serviceAccountName) {
    const { audience } = useInstance()
    const { getServiceAccountToken } = useServiceAccount()
    const token = await getServiceAccountToken({
      name: serviceAccountName,
      audience: audience.value as string
    })

    config.headers['Authorization'] = `Bearer ${token}`
    return config
  }
  return (config = await configureAxiosRequestInterceptor(getPulsarAccessToken, config))
})

const getWorkspaceBasePath = (org: string, workspaceUid: string): string =>
  getProxyLocation() + `/workspace/${org}/workspace-${workspaceUid}`

export const getRegistryBase = () => {
  const { organization, workspaceUid } = usePulsarState()
  if (!organization.value || !workspaceUid.value) {
    throw new Error('Organization or workspaceUid is not set')
  }
  const basePath = getWorkspaceBasePath(organization.value, workspaceUid.value)
  return { axios: authInterceptedInstance, basePath: `${basePath}/v1/registry` }
}

export const getRegistryBaseForWorkspace = (org?: string, wsId?: string) => {
  const { organization, workspaceUid } = usePulsarState()
  const basePath = getWorkspaceBasePath(
    org || (organization.value as string),
    wsId || (workspaceUid.value as string)
  )
  return { axios: authInterceptedInstance, basePath: `${basePath}/v1/registry` }
}
